import { useMemo, useState, type ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { EndpointsTable } from '@/components/Inventory/EndpointsTable';
import type {
  GroupedAlert,
  GroupedAlertEndpoint,
} from '@kirin/api-client/contracts/alerts';
import { getUserDisplayName } from '@/lib/utils';
import { cn } from '@/lib/utils';

function endpointLabel(ep: GroupedAlertEndpoint): string {
  return ep.name || ep.machineId;
}

/**
 * KIR-3035 / KIR-3911: alerts-table "Endpoints" cell. Single endpoint → its
 * name; multiple → "N endpoints". The WHOLE cell is clickable (KIR-3911) and
 * opens the endpoint/user breakdown (Endpoint · User · Alerts, one row per
 * unique (endpoint, user) pair) — including "No endpoint recorded" rows for
 * occurrences from older agents that carry no endpoint. "Show Endpoints" opens
 * the shared full-details endpoints table (no Inventory link).
 */
export function AlertEndpointsCell({
  alert,
}: {
  alert: GroupedAlert;
}): ReactElement {
  const endpoints = alert.endpoints ?? [];
  const unrecorded = alert.endpointsUnrecorded ?? [];
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // userId → display name, from the alert's own users list (same resolution as
  // the Users column). The endpoint/unrecorded `userId` is the INTERNAL
  // users.id, whereas alert.users[].id is the Descope id once resolved (the
  // internal id then lives on `internalId`) — so index by both to match either.
  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of alert.users) {
      const name = getUserDisplayName(u);
      map.set(u.id, name);
      if (u.internalId) map.set(u.internalId, name);
    }
    return map;
  }, [alert.users]);

  const displayUser = (userId: string | null): string =>
    userId ? (userNameById.get(userId) ?? userId) : 'Unknown';

  const hasAny = endpoints.length > 0 || unrecorded.length > 0;
  if (!hasAny) {
    return <span className="text-muted-foreground">—</span>;
  }

  // Label counts recorded endpoints only — unrecorded rows are "no endpoint"
  // per-user buckets, not endpoints. When there are none, say so.
  const first = endpoints[0];
  const isSingle = endpoints.length === 1 && unrecorded.length === 0;
  const label =
    isSingle && first
      ? endpointLabel(first)
      : endpoints.length > 0
        ? `${endpoints.length} endpoint${endpoints.length === 1 ? '' : 's'}`
        : 'No endpoint recorded';

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Affected endpoints"
            className={cn(
              'flex w-full items-center gap-0.5 min-w-0 text-left underline decoration-dotted underline-offset-2 cursor-pointer hover:text-foreground'
            )}
            onClick={e => e.stopPropagation()}
          >
            <span className="truncate">{label}</span>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-96 p-0"
          onClick={e => e.stopPropagation()}
        >
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b">
            Affected Endpoints
          </div>
          <div className="max-h-64 overflow-auto">
            <Table className="text-xs">
              <TableHeader className="sticky top-0 bg-muted">
                <TableRow className="!bg-transparent hover:!bg-transparent">
                  <TableHead>Endpoint</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Alerts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map(ep => (
                  <TableRow
                    key={`${ep.userId ?? 'unknown'}-${ep.machineId}`}
                    className="border-b"
                  >
                    <TableCell className="truncate max-w-[10rem]">
                      {endpointLabel(ep)}
                    </TableCell>
                    <TableCell className="truncate max-w-[8rem]">
                      {displayUser(ep.userId)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {ep.occurrenceCount}
                    </TableCell>
                  </TableRow>
                ))}
                {unrecorded.map(u => (
                  <TableRow
                    key={`unrecorded-${u.userId ?? 'unknown'}`}
                    className="border-b"
                  >
                    <TableCell className="italic text-muted-foreground">
                      No endpoint recorded
                    </TableCell>
                    <TableCell className="truncate max-w-[8rem]">
                      {displayUser(u.userId)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {u.occurrenceCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {endpoints.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={() => {
                  setPopoverOpen(false);
                  setDialogOpen(true);
                }}
              >
                Show Endpoints
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-4xl"
          onClick={e => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>Endpoint Details</DialogTitle>
          </DialogHeader>
          <EndpointsTable endpoints={endpoints} showAlerts />
        </DialogContent>
      </Dialog>
    </>
  );
}

AlertEndpointsCell.displayName = 'AlertEndpointsCell';
